import { api } from './api';
import type {
  MessageResponse,
  OnDemandItemInput,
  OnDemandItemUpdate,
  ReviewItem,
  ReviewItemDetailResponse,
  ReviewLifecycleResponse,
  ReviewReportDelivery as ContractReviewReportDelivery,
  ReviewTemplate,
  Room,
} from '@exhale/contracts';
import type { HomeReview, ReviewContactDetails, ReviewResponse } from '../types';
import { FULL_REVIEW_TEMPLATES_PATH } from './templateApiPaths';

export type ReviewDetailsOptions = {
  includePhotos?: boolean;
  presignPhotos?: boolean;
  includeTemplate?: boolean;
};

export type ReviewItemDetail = ReviewItemDetailResponse;
export type { OnDemandItemInput, OnDemandItemUpdate } from '@exhale/contracts';

export type ReviewReportDelivery = ContractReviewReportDelivery;

export interface ReviewReportResendResult {
  delivery?: ReviewReportDelivery;
  message?: string;
}

const buildReviewDetailsPath = (
  reviewId: number,
  options?: ReviewDetailsOptions
): string => {
  const params = new URLSearchParams();

  if (options?.includePhotos === false) {
    params.append('includePhotos', 'false');
  }
  if (options?.presignPhotos === false) {
    params.append('presignPhotos', 'false');
  }
  if (options?.includeTemplate !== undefined) {
    params.append('includeTemplate', String(options.includeTemplate));
  }

  const query = params.toString();
  return query ? `/reviews/${reviewId}?${query}` : `/reviews/${reviewId}`;
};

const buildResponseWritePayload = (
  data: Partial<ReviewResponse>
): Record<string, unknown> => {
  if (!Array.isArray(data.findings)) {
    return data;
  }

  return {
    ...data,
    findings: data.findings.map(finding => ({
      ...(typeof finding.id === 'number' && finding.id > 0
        ? { id: finding.id }
        : {}),
      questionId: finding.questionId,
      severity: finding.severity ?? null,
      actionability: finding.actionability ?? null,
      ...(finding.clientId ? { clientId: finding.clientId } : {}),
      ...(finding.tempId ? { tempId: finding.tempId } : {}),
    })),
  };
};

export const reviewCoreApi = {
  getUserReviews(): Promise<HomeReview[]> {
    return api.get<HomeReview[]>('/reviews');
  },

  getTemplates(): Promise<ReviewTemplate[]> {
    return api.get<ReviewTemplate[]>(FULL_REVIEW_TEMPLATES_PATH);
  },

  getTemplateDetails(templateId: number): Promise<ReviewTemplate> {
    return api.get<ReviewTemplate>(`/reviews/templates/${templateId}`);
  },

  getReviewDetails(
    reviewId: number,
    options?: ReviewDetailsOptions
  ): Promise<HomeReview> {
    return api.get<HomeReview>(buildReviewDetailsPath(reviewId, options));
  },

  getReviewSnapshot(reviewId: number): Promise<HomeReview> {
    return api.get<HomeReview>(
      `/reviews/${reviewId}?includePhotos=false&presignPhotos=false&includeTemplate=true`
    );
  },

  getReviewTemplate(reviewId: number): Promise<ReviewTemplate> {
    return api.get<ReviewTemplate>(`/reviews/${reviewId}/template`);
  },

  createReview(
    propertyId: number,
    templateId: number,
    address?: string,
    details?: ReviewContactDetails
  ): Promise<HomeReview> {
    return api.post<HomeReview>('/reviews', {
      propertyId,
      templateId,
      address,
      ...details,
    });
  },

  updateReviewDetails(
    reviewId: number,
    details: ReviewContactDetails
  ): Promise<HomeReview> {
    return api.patch<HomeReview>(`/reviews/${reviewId}/details`, details);
  },

  async getReviewReportDeliveries(reviewId: number): Promise<ReviewReportDelivery[]> {
    const result = await api.get<{ deliveries: ReviewReportDelivery[] }>(
      `/reviews/${reviewId}/report-deliveries`
    );
    return result.deliveries;
  },

  resendReviewReport(reviewId: number): Promise<ReviewReportResendResult> {
    return api.post<ReviewReportResendResult>(
      `/reviews/${reviewId}/report-deliveries/resend`
    );
  },

  getReviewItemDetail(reviewId: number, itemId: number): Promise<ReviewItemDetail> {
    return api.get<ReviewItemDetailResponse>(`/reviews/${reviewId}/items/${itemId}`);
  },

  updateResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    return api.put<ReviewResponse>(
      `/reviews/${reviewId}/responses/${itemId}`,
      buildResponseWritePayload(data)
    );
  },

  updateSubmittedResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    return api.patch<ReviewResponse>(
      `/reviews/${reviewId}/responses/${itemId}`,
      buildResponseWritePayload(data)
    );
  },

  submitReview(
    reviewId: number,
    vendorNotes?: string
  ): Promise<ReviewLifecycleResponse> {
    const data = vendorNotes ? { vendorNotes } : undefined;
    return api.post<ReviewLifecycleResponse>(`/reviews/${reviewId}/submit`, data);
  },

  completeReview(reviewId: number): Promise<ReviewLifecycleResponse> {
    return api.post<ReviewLifecycleResponse>(`/reviews/${reviewId}/complete`, {});
  },

  searchReviewItems(query: string, reviewId?: number): Promise<ReviewItem[]> {
    const params = new URLSearchParams({ q: query });
    if (reviewId) {
      params.append('reviewId', reviewId.toString());
    }
    return api.get<ReviewItem[]>(`/reviews/search?${params}`);
  },

  getRooms(propertyId?: number): Promise<Room[]> {
    const url = propertyId ? `/rooms/property/${propertyId}` : '/rooms/defaults';
    return api.get<Room[]>(url);
  },

  createRoom(propertyId: number, name: string): Promise<Room> {
    return api.post<Room>(`/rooms/property/${propertyId}`, { name });
  },

  updateRoom(roomId: number, data: { name?: string; displayOrder?: number }): Promise<Room> {
    return api.put<Room>(`/rooms/${roomId}`, data);
  },

  async deleteRoom(roomId: number): Promise<void> {
    await api.delete<MessageResponse>(`/rooms/${roomId}`);
  },

  async deleteReview(reviewId: number): Promise<void> {
    await api.delete<MessageResponse>(`/reviews/${reviewId}`);
  },

  getOnDemandItems(reviewId: number): Promise<ReviewItem[]> {
    return api.get<ReviewItem[]>(`/reviews/${reviewId}/on-demand-items`);
  },

  createOnDemandItem(reviewId: number, data: OnDemandItemInput): Promise<ReviewItem> {
    return api.post<ReviewItem>(`/reviews/${reviewId}/on-demand-items`, data);
  },

  updateOnDemandItem(
    reviewId: number,
    itemId: number,
    data: OnDemandItemUpdate
  ): Promise<ReviewItem> {
    return api.put<ReviewItem>(`/reviews/${reviewId}/on-demand-items/${itemId}`, data);
  },

  async deleteOnDemandItem(reviewId: number, itemId: number): Promise<void> {
    await api.delete<MessageResponse>(`/reviews/${reviewId}/on-demand-items/${itemId}`);
  },

  updateReviewNotes(reviewId: number, notes: string): Promise<HomeReview> {
    return api.patch<HomeReview>(`/reviews/${reviewId}/notes`, { notes });
  },

  updateReviewOnDemandProjects(
    reviewId: number,
    onDemandProjects: string
  ): Promise<HomeReview> {
    return api.patch<HomeReview>(`/reviews/${reviewId}/on-demand-projects`, {
      onDemandProjects,
    });
  },
};
